/**
 * Global user profile context (wraps entire app in index.tsx).
 *
 * Subscribes to users/{uid} and users/{uid}/tournaments/* when signed in.
 * profilePhase: idle → loading → ready | missing | error.
 * AuthLayout uses profilePhase to gate EnterNamePage and GameSessionProvider mounting.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { collection, doc, query } from 'firebase/firestore';
import type { User } from '@/models';
import type { TeamSelectionData } from '@/models/user';
import { FirebaseContext } from '@/session/FirebaseContext';
import { toDict } from '@/helpers';
import { snapshotSubscribeDoc, snapshotSubscribeQuery } from './snapshotSubscribe';
import type { ProfilePhase } from './types';

export type UserProfileContextValue = {
  userData: User | undefined;
  userProfileExists: boolean | undefined;
  teamSelectionData: TeamSelectionData;
  profilePhase: ProfilePhase;
  profileError: Error | null;
};

const defaultValue: UserProfileContextValue = {
  userData: undefined,
  userProfileExists: undefined,
  teamSelectionData: {},
  profilePhase: 'idle',
  profileError: null,
};

const UserProfileContext = createContext<UserProfileContextValue>(defaultValue);

export function UserProfileProvider({ children }: PropsWithChildren) {
  const ctx = useContext(FirebaseContext);
  const uid = ctx.user?.uid;

  const [userData, setUserData] = useState<User | undefined>();
  const [userProfileExists, setUserProfileExists] = useState<boolean | undefined>();
  const [teamSelectionData, setTeamSelectionData] = useState<TeamSelectionData>({});
  const [profilePhase, setProfilePhase] = useState<ProfilePhase>('idle');
  const [profileError, setProfileError] = useState<Error | null>(null);

  useEffect(() => {
    setUserData(undefined);
    setUserProfileExists(undefined);
    setTeamSelectionData({});
    setProfilePhase(uid ? 'loading' : 'idle');
    setProfileError(null);
  }, [uid]);

  useEffect(() => {
    if (!uid) {
      return;
    }

    return snapshotSubscribeQuery(
      query(collection(ctx.db, `users/${uid}/tournaments`)),
      'user tournaments',
      (snapshot) => {
        setTeamSelectionData(toDict(snapshot));
      },
      (error) => {
        setProfileError(error);
        setProfilePhase('error');
      },
    );
  }, [ctx.db, uid]);

  useEffect(() => {
    if (!uid) {
      return;
    }

    return snapshotSubscribeDoc(
      doc(ctx.db, 'users', uid),
      'user profile',
      (snapshot) => {
        setProfileError(null);
        setUserProfileExists(snapshot.exists());
        if (snapshot.exists()) {
          setUserData(snapshot.data() as User);
          setProfilePhase('ready');
        } else {
          setUserData(undefined);
          setProfilePhase('missing');
        }
      },
      (error) => {
        setProfileError(error);
        setProfilePhase('error');
      },
    );
  }, [ctx.db, uid]);

  const value = useMemo<UserProfileContextValue>(
    () => ({
      userData,
      userProfileExists,
      teamSelectionData,
      profilePhase,
      profileError,
    }),
    [userData, userProfileExists, teamSelectionData, profilePhase, profileError],
  );

  return <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>;
}

export function useUserProfile(): UserProfileContextValue {
  return useContext(UserProfileContext);
}
